/**
 * @graphql agg
 */
import gql from 'graphql-tag';
import { useEffect, useState } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
	type QueryResult,
} from 'react-apollo';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { type ServiceConfig, utils } from '@atlaskit/util-service-support';

import { useAccessRequests } from '@confluence/access-requests/entry-points/useAccessRequests.tsx';
import { usePublicLinkPageDataProvider } from '@confluence/public-links/entry-points/usePublicLinkPageDataProvider.ts';
import { usePageSpaceKey } from '@confluence/page-context';
import { createSingleQueryParamHook } from '@confluence/route-manager/entry-points/RouteState.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { fg } from '@confluence/feature-gating';
import { UNSAFE_expValEqualsNoExposure } from '@confluence/feature-experiments';

import { SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE } from '../Controller.tsx';
import type { ContentType } from '../../shareAndRestrictButton/ShareRestrictAndCopyCoordinator.tsx';
import type { QueriesState } from '../Controller.tsx';

import {
	type useQueriesShareAndRestrictDialogQuery as useQueriesShareAndRestrictDialogQuery$data,
	type useQueriesShareAndRestrictDialogQueryVariables as useQueriesShareAndRestrictDialogQuery$variables,
} from './__types__/useQueriesShareAndRestrictDialogQuery.ts';
import { dataToQueriesState } from './dataToQueriesState.ts';
import {
	type PublishModalRestrictionsInput,
	publishModalDataToQueriesState,
} from './publishModalDataToQueriesState.ts';
// eslint-disable-next-line import/no-cycle
import { usePreloadUserSuggestions } from './useUserSuggestions.ts';
// eslint-disable-next-line import/no-cycle
import { useGroupsAndTeamCountQuery } from './useGroupsCountQuery.ts';
import { useTeamData } from './useTeamData.ts';

const useQueriesShareAndRestrictDialogQuery = gql`
	query useQueriesShareAndRestrictDialogQuery(
		$contentId: ID!
		$status: [String]
		$draftShareId: String
		$spaceKey: String!
	) {
		content(id: $contentId, status: $status, draftShareId: $draftShareId) {
			nodes {
				id
				ancestors {
					id
					hasViewRestrictions
					links {
						webui
					}
				}
				hasInheritedRestrictions
				operations {
					operation
					targetType
				}
				restrictions {
					read {
						restrictions {
							userWithRestrictions(start: 0) {
								count
								nodes {
									type
									displayName
									email
									accountId
									profilePicture {
										path
									}
									hasSpaceViewPermission
									hasSpaceEditPermission
									permissionType
									restrictingContent {
										id
										links {
											webui
											base
										}
									}
								}
								pageInfo {
									hasNextPage
									endCursor
								}
							}
							group(start: 0) {
								count
								nodes {
									id
									name
									permissionType
									usageType
								}
								pageInfo {
									hasNextPage
									endCursor
								}
							}
						}
					}
					update {
						restrictions {
							userWithRestrictions(start: 0) {
								count
								nodes {
									type
									displayName
									email
									accountId
									profilePicture {
										path
									}
									hasSpaceViewPermission
									hasSpaceEditPermission
									permissionType
									restrictingContent {
										id
										links {
											webui
											base
										}
									}
								}
								pageInfo {
									hasNextPage
									endCursor
								}
							}
							group(start: 0) {
								count
								nodes {
									id
									name
									permissionType
									usageType
								}
								pageInfo {
									hasNextPage
									endCursor
								}
							}
						}
					}
				}
			}
		}
		user: confluence_atlassianUser(current: true) {
			id
			displayName
			emails {
				value
				isPrimary
			}
			photos {
				value
				isPrimary
			}
		}
		space(key: $spaceKey) {
			id
			currentUser {
				isAdmin
			}
		}
	}
`;

const DEFAULT_SHARE_SERVICE_URL = '/gateway/api';

export type RefetchQueries = QueryResult<
	useQueriesShareAndRestrictDialogQuery$data,
	useQueriesShareAndRestrictDialogQuery$variables
>['refetch'];

export type QueriesHookResponse =
	| {
			state: 'loading';
	  }
	| {
			state: 'error';
	  }
	| {
			state: 'loaded';
			queriesState: QueriesState;
			refetchQueries: RefetchQueries;
	  };

type ConfigResponse = {
	disableSharingToEmails?: boolean;
};

const useDraftShareId = createSingleQueryParamHook('draftShareId');

async function fetchConfig(cloudId: string): Promise<ConfigResponse> {
	const serviceConfig: ServiceConfig = { url: DEFAULT_SHARE_SERVICE_URL };
	const options = {
		path: `${fg('confluence_new_share_service_content_share') ? 'contentShare' : 'share'}/configuration`,
		queryParams: { cloudId },
		requestInit: { method: 'get' },
	};
	return utils.requestService(serviceConfig, options);
}

export const useQueries = ({
	contentId,
	contentType,
	publishModalRestrictions,
	spaceKey: inputSpaceKey,
	cloudId,
}: {
	contentId: string;
	contentType: ContentType;
	publishModalRestrictions?: PublishModalRestrictionsInput;
	spaceKey?: string;
	cloudId: string;
}): QueriesHookResponse => {
	const [defaultSpaceKey] = usePageSpaceKey();
	const spaceKey = inputSpaceKey || defaultSpaceKey;
	const draftShareId = useDraftShareId() ?? '';
	const { edition } = useSessionData();
	const [configData, setConfigData] = useState<ConfigResponse | null>(null);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { loading, error, data, refetch } = useQuery<
		useQueriesShareAndRestrictDialogQuery$data,
		useQueriesShareAndRestrictDialogQuery$variables
	>(useQueriesShareAndRestrictDialogQuery, {
		variables: {
			contentId,
			// In order to allow us to fetch any data for draft content always we need to specify the status
			status: ['current', 'draft', 'archived'],
			draftShareId,
			spaceKey: spaceKey || '',
		},
		fetchPolicy: 'network-only',
		notifyOnNetworkStatusChange: true,
	});

	useEffect(() => {
		fetchConfig(cloudId)
			.then((result) => {
				setConfigData(result);
			})
			.catch((err) => {
				createAnalyticsEvent({
					type: 'sendOperationalEvent',
					data: {
						source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
						action: 'errored',
						actionSubject: 'disableSharingToEmails',
						attributes: {
							message: err?.message,
							name: err?.name,
							stack: err?.stack,
							cause: err?.cause,
						},
					},
				}).fire();
			});
	}, [cloudId, createAnalyticsEvent]);

	const publicLinkPageData = usePublicLinkPageDataProvider({
		pageId: contentId,
		fetchPolicy: 'network-only',
	});

	usePreloadUserSuggestions();

	useEffect(() => {
		if (error) {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
					action: 'errored',
					actionSubject: 'shareAndRestrictDialogQuery',
					attributes: {
						message: error?.message,
						name: error?.name,
						stack: error?.stack,
						cause: error?.cause,
					},
				},
			}).fire();
		}
	}, [error, createAnalyticsEvent]);

	const { isGroupsCountLoading, groupAndTeamCountsMap } = useGroupsAndTeamCountQuery({
		data,
		publishModalRestrictions,
		error,
	});

	const { teamData, isTeamDataLoading } = useTeamData(cloudId, publishModalRestrictions, data);

	const { requests: requestedUsersPendingApproval, loading: isAccessRequestsLoading } =
		useAccessRequests(
			'network-only',
			SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
			publishModalRestrictions !== undefined ||
				!fg('twcg-444-invite-usd-improvements-m2-gate') ||
				!UNSAFE_expValEqualsNoExposure(
					'twcg-444-invite-usd-improvements-m2',
					'cohort',
					'variation',
				),
			'PENDING_SITE_APPROVAL',
		);

	if (loading || isGroupsCountLoading || isTeamDataLoading || isAccessRequestsLoading)
		return { state: 'loading' };

	if (error) {
		// We never expect any errors here. Don't mark it as handled.
		return { state: 'error' };
	}

	// Translate the query to our internal state. If that was unsuccessful
	// (most likely because of insufficient data), consider it an error
	if (!data) return { state: 'error' };

	const queriesState = (() => {
		if (publishModalRestrictions) {
			return data && edition
				? publishModalDataToQueriesState({
						publishModalRestrictions,
						spaceKey: spaceKey || '',
						data,
						publicLinkPageData,
						contentType,
						edition,
						groupAndTeamCountsMap,
						teamData,
					})
				: undefined;
		}
		return data && edition
			? dataToQueriesState(
					data,
					publicLinkPageData,
					spaceKey || '',
					contentType,
					edition,
					configData?.disableSharingToEmails,
					groupAndTeamCountsMap,
					teamData,
					requestedUsersPendingApproval,
				)
			: undefined;
	})();

	if (!queriesState) return { state: 'error' };

	return { state: 'loaded', queriesState, refetchQueries: refetch };
};
